import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import {
  cancelSubscription,
  openBillingPortal,
  RECURRING_SUBSCRIPTION_TIERS,
} from '../services/stripeService';
import { isEducatorPilotTier, tierDisplayLabel } from '../types/tiers';

interface ManageSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ManageSubscriptionModal: React.FC<ManageSubscriptionModalProps> = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const { subscription, markSubscriptionCancelled, syncSubscriptionFromDB } = useSubscription();
  const [loading, setLoading] = useState(false);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);

  const isRecurring = RECURRING_SUBSCRIPTION_TIERS.includes(subscription.tier);
  const isDayPass = subscription.tier === 'day_pass';
  const isPilot = isEducatorPilotTier(subscription.tier);
  const accessEndsAt = subscription.expiresAt
    ? new Date(subscription.expiresAt).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : null;

  if (!isOpen) return null;

  const handleCancel = async () => {
    if (!user) return;

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const result = await cancelSubscription();

      markSubscriptionCancelled(result.currentPeriodEnd);
      await syncSubscriptionFromDB();

      const endDate = result.currentPeriodEnd
        ? new Date(result.currentPeriodEnd).toLocaleDateString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
          })
        : accessEndsAt;

      setSuccess(
        result.alreadyCancelled
          ? `Your subscription is already set to cancel. You'll keep access until ${endDate}.`
          : `Subscription cancelled. You'll keep Pro access until ${endDate}.`
      );
      setConfirmCancel(false);
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Failed to cancel subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPortal = async () => {
    if (!user) return;

    setLoadingPortal(true);
    setError(null);

    try {
      await openBillingPortal();
    } catch (err) {
      console.error(err);
      setError(
        err instanceof Error
          ? err.message
          : 'Could not open billing portal. You can still cancel below.'
      );
      setLoadingPortal(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="px-6 py-5" style={{ backgroundColor: '#15452f' }}>
          <h2 className="text-2xl font-bold text-white">
            {isPilot ? 'Account access' : 'Manage Subscription'}
          </h2>
          <p className="text-emerald-100 mt-1">
            {isPilot ? 'View your Complimentary Pilot Access' : 'View your plan and billing options'}
          </p>
        </div>

        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          {success && (
            <div className="p-3 bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-700 rounded-lg text-emerald-700 dark:text-emerald-300 text-sm">
              {success}
            </div>
          )}

          <div className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-slate-500 dark:text-slate-400">Current plan</span>
              <span className="font-medium text-slate-800 dark:text-slate-100">
                {isPilot ? 'Educator / Faculty Pilot' : tierDisplayLabel(subscription.tier)}
              </span>
            </div>
            {accessEndsAt && (
              <div className="flex justify-between text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                  {isPilot
                    ? 'Pilot access ends'
                    : subscription.cancelAtPeriodEnd
                      ? 'Access ends'
                      : 'Renews / expires'}
                </span>
                <span className="font-medium text-slate-800 dark:text-slate-100">{accessEndsAt}</span>
              </div>
            )}
            {isPilot && !accessEndsAt && (
              <p className="text-sm text-slate-600 dark:text-slate-300">
                Complimentary Pilot Access is active with no scheduled end date.
              </p>
            )}
            {subscription.cancelAtPeriodEnd && !isPilot && (
              <p className="text-sm text-amber-700 dark:text-amber-300">
                Your subscription is set to cancel. You will not be charged again.
              </p>
            )}
          </div>

          {isPilot && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              This account has Educator / Faculty Pilot access. No payment method is required.
              You&apos;re helping us improve the AI Preparation Layer for Education.
              Contact Redact AI support if you need changes to your access, or{' '}
              <a
                href="mailto:support@thelearningindex.com?subject=Redact%20AI%20Pilot%20Feedback&body=Did%20Redact%20AI%20prepare%20the%20entire%20document%20correctly%3F%0A%0ADid%20you%20notice%20any%20missed%20sensitive%20information%3F%0A%0AWhat%20type%20of%20workflow%20are%20you%20preparing%20this%20document%20for%3F%0A%0A(Please%20do%20not%20include%20student%20names%20or%20document%20contents.)"
                className="text-emerald-700 dark:text-emerald-400 hover:underline"
              >
                share feedback
              </a>
              .
            </p>
          )}

          {isDayPass && (
            <p className="text-sm text-slate-600 dark:text-slate-300">
              Day Pass is a one-time purchase with no recurring subscription to cancel.
              {accessEndsAt ? ` Your access ends on ${accessEndsAt}.` : ''}
            </p>
          )}

          {isRecurring && !subscription.cancelAtPeriodEnd && (
            <>
              {!confirmCancel ? (
                <div className="space-y-3">
                  <button
                    type="button"
                    onClick={handleOpenPortal}
                    disabled={loadingPortal}
                    className="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {loadingPortal ? 'Opening…' : 'Manage billing in Stripe'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmCancel(true)}
                    className="w-full py-2 px-4 text-sm text-red-600 dark:text-red-400 hover:underline"
                  >
                    Cancel subscription
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-600 dark:text-slate-300">
                    Cancel at the end of your billing period? You will keep Pro access until{' '}
                    {accessEndsAt || 'the end of the period'}, and you will not be charged again.
                  </p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setConfirmCancel(false)}
                      disabled={loading}
                      className="flex-1 py-3 px-4 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 font-semibold rounded-lg hover:bg-slate-50 dark:bg-slate-700 transition-colors"
                    >
                      Keep subscription
                    </button>
                    <button
                      type="button"
                      onClick={handleCancel}
                      disabled={loading}
                      className="flex-1 py-3 px-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {loading ? 'Cancelling…' : 'Confirm cancel'}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {isRecurring && subscription.cancelAtPeriodEnd && !success && (
            <button
              type="button"
              onClick={handleOpenPortal}
              disabled={loadingPortal}
              className="w-full py-3 px-4 bg-emerald-600 text-white font-semibold rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
            >
              {loadingPortal ? 'Opening…' : 'Reactivate in Stripe'}
            </button>
          )}
        </div>

        <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-t border-slate-200 dark:border-slate-700">
          <button
            type="button"
            onClick={onClose}
            className="w-full py-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
          >
            Close
          </button>
        </div>

        <button onClick={onClose} className="absolute top-4 right-4 text-white/80 hover:text-white">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ManageSubscriptionModal;
